import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Platform } from '@ionic/angular';
import { catchError, map, switchMap } from 'rxjs/operators';
import { defer, Observable, race, throwError, timer } from 'rxjs';
import { fromEvent, merge, of, Subscription } from 'rxjs';

import { CacheService } from 'uServices/cache.service';
import { Constants } from 'uApp/app.constants';
import { environment } from 'uEnvironments/environment';
import { SpinnerService } from 'uServices/spinner.service';
import { SystemService } from 'uServices/system.service';
import { ToastService } from 'uServices/toast.service';
import { ISysData } from 'uInterfaces/sysdata';
import { EventsService } from 'uServices/events.service';


export interface IRunRequest {
  dismissOnPageChange?: boolean;
  handleError?: boolean;
  includeSystem?: boolean;
  includeToken?: boolean;
  includeCaptcha?: boolean;
  method: string;
  request: any;
  shopAPI?: boolean;
  showError?: boolean;
  spinner: boolean;
  timeOutRequest?: number;
}

export interface IErrorResponse {
  error: string;
  headers?: object;
  status: number;
  url: string;
}

@Injectable({
  providedIn: 'root'
})
export class ApiService {
  private requestOptions: any;
  private API_URL = environment.apiUrl;
  private API_SHOP_URL = environment.apiShopUrl;
  // private sslCheck = environment.sslCheck;
  options: any = {
    headers: new HttpHeaders({
      'Content-Type': Constants.CONTENT_TYPE,
      'encoding': Constants.ENCODING,
      'timeout': (Constants.HTTP_REQUEST_DURATION).toString(),
      'origin': 'file://'
    })
  };
  networkStatus: boolean = false;
  networkStatus$: Subscription = Subscription.EMPTY;
  private show404: any;
  public localeValues: any = {};
  public defaultError = 'Виникла помилка. Спробуйте виконати операцію ще раз';

  constructor(public cache: CacheService,
              public events: EventsService,
              public http: HttpClient,
              public ngZone: NgZone,
              public platform: Platform,
              public toast: ToastService,
              public spinner: SpinnerService,
              private system: SystemService) {
    this.init();
  }

  public async init() {
    // console.log('ApiService:  init start: ' + new Date().toLocaleTimeString());
    await this.platform.ready();
    const headers = {
      headers: new HttpHeaders(
      {
        'Content-Type': 'application/json',
        // 'rejectUnauthorized': 'false',
        // 'encoding': 'UTF-8',
        // 'timeout': (Constants.HTTP_REQUEST_DURATION).toString(),
        // 'requestCert': 'false',
        // 'insecure': 'true',
        // 'Access-Control-Allow-Origin': '*',
        // 'Access-Control-Allow-Credentials': 'true',
        // 'Access-Control-Allow-Methods': 'GET, POST, PUT, PATCH, POST, DELETE, OPTIONS',
        // 'Access-Control-Allow-Headers': 'Content-Type',
        // 'Access-Control-Max-Age': '86400',
        // 'X-Requested-With': 'XMLHttpRequest'
      })
    };
    this.requestOptions = headers;
    // this.http.setDataSerializer('json');
    // TODO - delete in Production
    // if (!this.sslCheck) { this.http.setServerTrustMode('nocheck'); }
    // console.log('ApiService: init done');
    // console.log('ApiService:  init finish: ' + new Date().toLocaleTimeString());
    this.checkNetworkStatus();
  }

  public checkNetworkStatus() {
    this.networkStatus = navigator.onLine;
    this.networkStatus$ = merge(
      of(null),
      fromEvent(window, 'online'),
      fromEvent(window, 'offline')
    )
      .pipe(map(() => navigator.onLine))
      .subscribe(status => {
        console.log('Network status', status);
        this.networkStatus = status;
      });
  }

  public uncheckNetworkStatus(): void {
    if (this.networkStatus$) { this.networkStatus$.unsubscribe(); }
  }

  public nullSanitize = (request: any) => {
    let tempArr: any[];
    for (const key in request) {
      if (request.hasOwnProperty(key)) {
        if (request[key] === null || request[key] === undefined) { request[key] = ''; }
        if (typeof request[key] === 'object') {
          if (Array.isArray(request[key])) {
            tempArr = request[key].map((item: any) => this.nullSanitize(item));
            request[key] = tempArr;
          } else {
            request[key] = this.nullSanitize(request[key]);
          }
        }
      }
    }
    return request;
  };

  public runRequest({method, request, spinner, timeOutRequest = Constants.HTTP_REQUEST_DURATION,
                      shopAPI = false, showError = true, includeToken = true, handleError = true,
                      includeSystem = true, includeCaptcha = false}: IRunRequest): Observable<any> {
    this.networkStatus = navigator.onLine;
    const URL = shopAPI ? this.API_SHOP_URL : this.API_URL;
    if (!this.networkStatus) {
      const thisDT: any = new Date();
      if (!this.show404 || (thisDT - this.show404) > Constants.TIMEOUT_ERROR_NOT_NETWORK) {
        this.show404 = new Date();
        this.apiHandlerError('Ionic', { Code: 1 }, method, showError);
      }
      return throwError(() => { return {errorMessage: 'Network Error', errorCode: 404}});
    }
    const expires = (timeout: number) => (source: any) => {
      const error = throwError(() => new Error('timeout'));
      return race(source, timer(timeout).pipe(switchMap(() => error)));
    };
    // let httpDeferred;
    console.log('>>>S-API>>> runRequest - networkStatus: ' + this.networkStatus);
    const httpDeferred = (url: string, requestBody: any) =>
      defer(() => this.http.post(url, requestBody, this.requestOptions));

    console.log('>>>S-API>>> runRequest - method:' + method + ', request:', JSON.stringify(request));
    const nTimeOutRequest = timeOutRequest*1 || 10000;
    if (spinner) { this.spinner.showSpinner({}); }
    // this.http.setDataSerializer('json');
    let sysObj$;
    if (includeSystem) {
      sysObj$ = this.system.createNewSystemObjObs(includeToken)
        .pipe(map((sysObj: ISysData) => {
          request.body.system = sysObj;
          if (includeToken) {
            const token = sysObj.token;
            // console.log('>>>S-API>>> runRequest - token:', token);
            if (!token) {
              const error = {
                error: {
                  errorMessage: 'Expected Token not found',
                  errorCode: '4011',
                  endSession: 'Y'
                }
              };
              console.log('>>>S-API>>> runRequest - no token in system, but expected: abort request');
              throw(error);
            }
          }
          return request;
        }));
    } else {
      sysObj$ = of(request);
    }

    return sysObj$.pipe(
      switchMap((request: any) => {
        if (includeCaptcha) {
          const recaptcha = this.cache.getData('app-recaptcha');
          if (includeSystem) { request.body.system.recaptcha = recaptcha; }
          else { request.body.system = { recaptcha }; }
        }
        console.log('>>>S-API>>> runRequest - request:', JSON.stringify(request));
        return httpDeferred(URL + method, request)
          .pipe(
            expires(nTimeOutRequest),
            catchError((error) => {
              if (handleError) { this.handleError(error, method, request, showError); }
              return throwError(() => error);
            }),
            map((response: any) => {
              console.log('>>>S-API>>> httpDeferred response: ', response);

              /* if (res && res.info?.updateRequired === 'Y') {
                const error = res.info;
                error.status = -5;
                this.handleError(error, method, request, true);
              } */
              /* if (res && res.error?.errorCode === '4099') {  // Планові роботи
                console.log('>>>S-API>>> runRequest - siebel repair:', res.error.errorMessage);
                const message = res.error.errorMessage || 'Unknown error, code: 4099';
                this.events.publish('app:sys_failure', JSON.stringify({type:'siebel-repair', message}));
                throw(res);
              } else */
              if (response && response.error?.endSession === 'Y') { // token expired
                console.log('>>>S-API>>> runRequest - endSession:', response.error.errorMessage);
                const data = { type: 'endSession', message: response.error.errorMessage };
                this.events.publish('app:logout', JSON.stringify(data));
                throw(response);
              } /*else {*/

              return response;
              /* } */
            }));
      })
    );
  }

  public handleError(response: IErrorResponse | any, method: any = '', request: any = {}, showError: boolean = true) {
    // @ts-ignore
    if (response === 'timeout') { response = 'Виникла помилка. Спробуйте виконати операцію ще раз.'; }

    // if (response.headers && response.headers['content-type']?.indexOf('json') !== -1) {
    //   const data = response.data && JSON.parse(response.data);
    // } else {
    //   const data = { error: { errorMessage:'Server error', errorCode: '500' } };
    // }

    console.log('>>>S-API>>handleError(' + method + '):', response, 'Request', request);
    const status: number = response.status || 0;
    const errorPart = response.error;
    let responseCode: string = '';
    // let errorObj;
    try {
      const errorCode = errorPart && JSON.parse(errorPart).code;
      responseCode = '' + errorCode;
    } catch (e) {
      // errorObj = {};
    }

    let errType = 'Siebel';
    console.log('>>>S-API>>handleError(' + method + '): errType - ',errType, ', status - ', status);
    switch (status) {
      case 401:
        // if (responseCode === '401' || responseCode === '4011') { this.events.publish('app:logout', ''); }
        if (responseCode === '45001') { showError = false; }  // Wrong OTP-code - don`t show toaster
        this.apiHandlerError('Siebel', response, method, showError);
        break;
      case 403:
        errType = (responseCode === '45000') ? 'Siebel' : 'Ionic';
        this.apiHandlerError(errType, response, method, showError);
        break;
      case 500:
        this.apiHandlerError('Siebel', response, method, showError);
        break;
      case 504:
        this.apiHandlerError('Siebel', {error: JSON.stringify({ERROR:'Сервер недоступний.'})}, method, showError);
        break;
      case -1:
        this.apiHandlerError('Ionic',
          {message: 'Сервер недоступний. Перевірте доступ до мережі інтернет.', Cod: 10}, method, showError);
        break;
      default:
        this.apiHandlerError('Ionic', response, method, showError);
    }
  }

  apiHandlerError(type: any, errorData: any | IErrorResponse = {ResponseText: 'Server error', ResponseCode: 'undefined'},
               method: string='', showError: boolean=true) {
    console.log('>>>S_Api_Handler_error>>> HandlerError: method-' , method, ', Type-', type, ', ErrorData-', errorData);
    if (!showError) { return; }
    switch (type) {
      case 'Siebel':
        let parse;
        try { parse = JSON.parse(errorData.error); }
          catch (e) { console.log('>>>S_Api_Handler_error>>> HandlerError: parse error-' , e); }
        const message = (parse && parse.ERROR) || this.defaultError;
        this.toast.showToast({ message });
        break;
      case 'Ionic':
        let messageIonic = '';
        switch (errorData.Code) {
          case 666:
            messageIonic = this.defaultError;
            break;
          case 1:
            messageIonic = this.localeValues.noConnectToNetwork || 'Відсутнє підключення до мережі.';
            break;
          case 2:
            messageIonic = this.localeValues.getStatusError;
            break;
          case 3:
            messageIonic = this.localeValues.noGeoLocation;
            break;
          case 4:
            messageIonic = this.localeValues.noFileType;
            break;
          case 5:
            messageIonic = this.localeValues.noOpenFileType;
            break;
          case 6:
            messageIonic = this.localeValues.noOpenFile;
            break;
          case 7:
            messageIonic = this.localeValues.noSaveFile;
            break;
          case 8:
            messageIonic = this.localeValues.restoreError;
            break;
          case 9:
            messageIonic = this.localeValues.recordNotFound;
            break;
          case 10:
            messageIonic = this.localeValues.noConnectionToServer;
            break;
          default:
            //"Код ошибки не определен"
            messageIonic = this.defaultError;
            break;
        }
        this.toast.showToast({
          message: messageIonic
        });
        break;
      case 'Data':
        //Добавить сообщение об ошибке в Сиебель
        this.toast.showToast({
          message: errorData.Msg
        });
        break;
      default:
        this.toast.showToast({
          message: this.defaultError // "Тип ошибки не определен"
        });
        break;
    }
  }

  /* public runRequest({method, request, spinner, timeOutRequest = Constants.HTTP_REQUEST_DURATION,
                    showError = true, includeCaptcha = false, includeToken = true, handleError = true,
                    includeSystem = true}: IRunRequest): Observable<any> {
    return new Observable((observer: any) => {
      const expires = (timeout: number) => (source: any) => {
        const error = throwError('timeout');
        return race(source, timer(timeout).pipe(switchMapTo(error)));
      };
      // let httpDeferred;
      const httpDeferred = (url: string, requestBody: any) => defer(() => this.http.post(url, requestBody, this.requestOptions));

      // if (includeToken) { request.token = this.cache.getData('app-token'); }
      console.log('>>>S-API>>> runRequest - method:' + method + ', request:', JSON.stringify(request));
      const nTimeOutRequest = timeOutRequest*1 || 10000;
      this.networkStatus = navigator.onLine;

      if (!this.networkStatus) {
        const thisDT: any = new Date();
        if (!this.show404 || (thisDT - this.show404) > Constants.TIMEOUT_ERROR_NOT_NETWORK) {
          this.show404 = new Date();
          this.apiHandlerError('Ionic', { Code: 1 }, method, showError);
        }
        observer.error({errorText: 'Network Error', errorCode: 404});
      } else {
        if (spinner) { this.spinner.showSpinner({}); }
        // this.http.setDataSerializer('json');
        // console.log('>>>S-API>> runRequest URL - ' + Constants.API_URL + method);
        this.ngZone.runTask(async () => {
          if (includeSystem) { request.body.system = await this.system.createNewSystemObj(includeToken); }
          if (includeCaptcha) {
            const recaptcha = this.cache.getData('app-recaptcha');
            if (includeSystem) {
              request.body.system.recaptcha = recaptcha;
            } else {
              request.body.system = { recaptcha };
            }
          }
          console.log('>>>S-API>>> runRequest - request:', JSON.stringify(request));

          httpDeferred(this.API_URL + method, request)
            .pipe(
              expires(nTimeOutRequest),
              //map((response: any) => JSON.parse(response.data))
              )
            .subscribe({next: (res: any) => {
                console.log('>>>S-API>>> httpDeferred response: ', res);
                observer.next(res);
                observer.complete();
              },
              error: error => {
                if (handleError) { this.handleError(error, method, request, showError); }
                observer.error(error);
              }});
        });
      }
    });
  } */

}
